import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Send, Copy, Loader2, AlertTriangle, CheckCircle2, MessageSquareText } from 'lucide-react';
import { useToast } from '../../src/contexts/ToastContext';
import { propsFundoModal } from '../../src/lib/backdropModal';
import { registrarRespostaCancelamento, MEIO_RESPOSTA_LABEL } from '../../src/lib/cancelamentos';
import type { MeioRespostaManual } from '../../src/lib/cancelamentos';
import type { SolicitacaoCancelamentoDetalhada } from '../../src/lib/cancelamentos';
import { lerAnaliseFinanceiraSalva } from '../../src/lib/analiseFinanceiraCancelamento';
import { montarMensagemRetorno, LIMITE_MENSAGEM_WHATSAPP, MODELO_PROVISORIO } from '../../src/lib/mensagemRetornoCancelamento';
import { enviarMensagemCancelamento, rotuloResultado } from '../../src/lib/envioCancelamento';
import type { EnvioRegistrado } from '../../src/lib/envioCancelamento';

interface Props {
    isOpen: boolean;
    solicitacao: SolicitacaoCancelamentoDetalhada | null;
    onClose: () => void;
    /** Resposta registrada: o pai recarrega a lista. */
    onRegistrado: () => void;
}

const fmtDataHora = (v: string) => new Date(v).toLocaleString('pt-BR');
/** Hoje em Brasília, AAAA-MM-DD — o `max` do campo de data. */
const hojeSP = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' });

/**
 * Prévia do retorno da análise (#5831). O envio automático por e-mail e WhatsApp
 * é a demanda 06 — enquanto ela não existe, o atendente copia o texto e registra
 * o envio aqui, o que grava o texto exato na solicitação e na linha do tempo.
 */
const RespostaCancelamentoModal: React.FC<Props> = ({ isOpen, solicitacao, onClose, onRegistrado }) => {
    const { addToast } = useToast();
    const [texto, setTexto] = useState('');
    const [salvando, setSalvando] = useState(false);
    const [enviando, setEnviando] = useState(false);
    const [resultadoEnvio, setResultadoEnvio] = useState<EnvioRegistrado[] | null>(null);
    // Resposta dada por fora (#6127): antes o registro saía sempre com a data do
    // clique, canal "manual" e o texto do rascunho — um WhatsApp de 14/09 ficava
    // registrado como resposta do dia em que alguém lembrou de marcar.
    const [porFora, setPorFora] = useState(false);
    const [dataPorFora, setDataPorFora] = useState(hojeSP());
    const [meioPorFora, setMeioPorFora] = useState<MeioRespostaManual | ''>('');
    const painelPorForaRef = useRef<HTMLDivElement>(null);
    // O quadro abre embaixo do texto: sem rolar, o botão de registrar fica fora da vista.
    useEffect(() => { if (porFora) painelPorForaRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }); }, [porFora]);

    const montada = useMemo(() => {
        if (!solicitacao) return null;
        return montarMensagemRetorno({
            clienteNome: solicitacao.cliente_nome,
            protocolo: solicitacao.protocolo,
            dataSolicitacao: solicitacao.data_solicitacao,
            dataRetornoPrevista: solicitacao.dataRetornoPrevista,
            analise: lerAnaliseFinanceiraSalva(solicitacao.validacaoItens),
        });
    }, [solicitacao]);

    useEffect(() => {
        if (isOpen && montada) setTexto(montada.texto);
    }, [isOpen, montada]);
    useEffect(() => {
        if (isOpen) { setPorFora(false); setDataPorFora(hojeSP()); setMeioPorFora(''); }
    }, [isOpen, solicitacao?.id]);

    if (!isOpen || !solicitacao || !montada) return null;

    const excedeWhatsapp = texto.length > LIMITE_MENSAGEM_WHATSAPP;
    const editado = texto.trim() !== montada.texto.trim();

    const copiar = async () => {
        try {
            await navigator.clipboard.writeText(texto);
            addToast('Mensagem copiada. Cole no WhatsApp ou no e-mail do cliente.', 'success');
        } catch {
            addToast('Não foi possível copiar. Selecione o texto e copie manualmente.', 'warning');
        }
    };

    /**
     * Envia pelos canais (#5830) e, se algum canal aceitou, carimba a resposta.
     * Falha em todos os canais NÃO marca como respondido: o cliente não recebeu.
     */
    const enviarAoCliente = async () => {
        if (!texto.trim()) {
            addToast('A mensagem não pode ficar vazia.', 'warning');
            return;
        }
        setEnviando(true);
        try {
            const envios = await enviarMensagemCancelamento(solicitacao.id, 'retorno', texto);
            setResultadoEnvio(envios);
            const aceitos = envios.filter(e => e.resultado === 'enviado' || e.resultado === 'enfileirado');
            if (aceitos.length === 0) {
                addToast('Nenhum canal aceitou o envio. Veja o motivo abaixo e tente de novo ou copie o texto.', 'error');
                return;
            }
            const canal = aceitos.length > 1 ? 'email_whatsapp' : (aceitos[0].canal === 'email' ? 'email' : 'whatsapp');
            await registrarRespostaCancelamento(solicitacao.id, texto, canal);
            addToast(`Mensagem enviada por ${aceitos.map(e => rotuloResultado(e.canal, e.resultado)).join(' e ')}.`, 'success');
            onRegistrado();
        } catch (e: any) {
            console.error('Erro ao enviar a resposta do cancelamento', e);
            addToast('Erro ao enviar: ' + (e.message || ''), 'error');
        } finally {
            setEnviando(false);
        }
    };

    const dataMinima = String(solicitacao.data_solicitacao || '').slice(0, 10);
    const registrar = async () => {
        if (!texto.trim()) {
            addToast('A mensagem não pode ficar vazia.', 'warning');
            return;
        }
        if (!meioPorFora) {
            addToast('Informe por onde a resposta foi enviada.', 'warning');
            return;
        }
        if (!dataPorFora || dataPorFora > hojeSP() || (dataMinima && dataPorFora < dataMinima)) {
            addToast('A data precisa estar entre o dia do pedido e hoje.', 'warning');
            return;
        }
        setSalvando(true);
        try {
            // Grava o texto EXATO que foi para o cliente — é ele que vale se o
            // cliente cobrar depois, não a prévia recalculada. Meio-dia de Brasília:
            // sem hora, a data "escorregaria" para o dia anterior no fuso.
            await registrarRespostaCancelamento(solicitacao.id, texto, 'manual', {
                enviadaEm: `${dataPorFora}T12:00:00-03:00`,
                meio: meioPorFora,
            });
            addToast('Resposta registrada na solicitação e no histórico.', 'success');
            onRegistrado();
            onClose();
        } catch (e: any) {
            console.error('Erro ao registrar a resposta do cancelamento', e);
            addToast('Erro ao registrar a resposta: ' + (e.message || ''), 'error');
        } finally {
            setSalvando(false);
        }
    };

    return createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-fade-in"
            {...propsFundoModal(() => { if (!salvando) onClose(); })}>
            <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[92vh]"
                onClick={e => e.stopPropagation()}>

                <div className="flex items-center justify-between p-4 md:p-6 border-b border-gray-100 dark:border-slate-700/50 bg-gray-50/50 dark:bg-slate-800/50">
                    <div className="flex items-center gap-3 min-w-0">
                        <div className="p-2 bg-primary/10 text-primary rounded-lg border border-primary/20 shrink-0">
                            <MessageSquareText size={20} />
                        </div>
                        <div className="min-w-0">
                            <h2 className="text-lg font-bold text-gray-800 dark:text-slate-100 font-poppins">Responder o cliente</h2>
                            <p className="text-sm text-gray-500 dark:text-slate-400 truncate">
                                {solicitacao.cliente_nome} — UC {solicitacao.uc_numero}
                                {solicitacao.protocolo ? ` · ${solicitacao.protocolo}` : ''}
                            </p>
                        </div>
                    </div>
                    <button onClick={onClose} disabled={salvando} aria-label="Fechar"
                        className="p-2 text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-lg transition-colors disabled:opacity-50">
                        <X size={20} />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-4">
                    {solicitacao.respostaEnviadaEm && (
                        <div className="rounded-xl border border-emerald-200 dark:border-emerald-800/50 bg-emerald-50/70 dark:bg-emerald-900/15 px-3.5 py-3 flex items-start gap-3">
                            <CheckCircle2 size={16} className="text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" aria-hidden="true" />
                            <p className="text-xs text-gray-700 dark:text-slate-300">
                                Este cliente já foi respondido em {solicitacao.respostaCanal === 'manual'
                                    // Resposta por fora guarda só o dia (o meio-dia é do sistema, não de quem respondeu).
                                    ? new Date(solicitacao.respostaEnviadaEm).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' })
                                    : fmtDataHora(solicitacao.respostaEnviadaEm)}. Registrar de novo substitui o texto guardado.
                            </p>
                        </div>
                    )}

                    {resultadoEnvio && (
                        <div role="status" aria-live="polite" className="rounded-xl border border-gray-200 dark:border-slate-700 bg-gray-50/60 dark:bg-slate-800/50 px-3.5 py-3">
                            <p className="text-[11px] font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wider mb-1.5">Resultado do envio</p>
                            <ul className="flex flex-col gap-1">
                                {resultadoEnvio.map((e, i) => (
                                    <li key={i} className="text-xs text-gray-700 dark:text-slate-300">
                                        {rotuloResultado(e.canal, e.resultado)}
                                        {e.detalhe ? <span className="text-gray-400 dark:text-slate-500"> — {e.detalhe}</span> : null}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {montada.avisos.map((aviso, i) => (
                        <div key={i} className="rounded-xl border border-amber-200 dark:border-amber-800/50 bg-amber-50/70 dark:bg-amber-900/15 px-3.5 py-3 flex items-start gap-3">
                            <AlertTriangle size={16} className="text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" aria-hidden="true" />
                            <p className="text-xs text-gray-700 dark:text-slate-300">{aviso}</p>
                        </div>
                    ))}

                    <div>
                        <div className="flex items-baseline justify-between gap-2 mb-1">
                            <label htmlFor="resposta-cancelamento" className="block text-[11px] font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                                Mensagem para o cliente
                            </label>
                            <span className={`text-[11px] ${excedeWhatsapp ? 'text-red-600 dark:text-red-400 font-bold' : 'text-gray-400 dark:text-slate-500'}`}>
                                {texto.length} caracteres
                            </span>
                        </div>
                        <textarea
                            id="resposta-cancelamento"
                            value={texto}
                            onChange={e => setTexto(e.target.value)}
                            rows={12}
                            className="w-full border border-gray-200 dark:border-slate-700 rounded-xl px-3 py-2.5 text-sm outline-none bg-white dark:bg-slate-900 dark:text-slate-100 focus:border-primary resize-y"
                        />
                        <p className="text-[11px] text-gray-400 dark:text-slate-500 mt-1.5">
                            Os valores vêm da conferência já validada, não de um cálculo novo.
                            {MODELO_PROVISORIO ? ' Texto provisório até o modelo ser aprovado na demanda 01.' : ''}
                            {editado ? ' Você alterou a prévia — o texto guardado será o que está aí.' : ''}
                        </p>
                        {excedeWhatsapp && (
                            <p className="text-[11px] text-red-600 dark:text-red-400 mt-1">
                                Acima de {LIMITE_MENSAGEM_WHATSAPP} caracteres o WhatsApp pode cortar a mensagem.
                            </p>
                        )}
                    </div>

                    {porFora ? (
                        <div ref={painelPorForaRef} className="rounded-xl border border-primary/30 bg-primary/5 px-4 py-3 space-y-3">
                            <div>
                                <p className="text-sm font-bold text-gray-800 dark:text-slate-100">Resposta enviada por fora da plataforma</p>
                                <p className="text-[11px] text-gray-500 dark:text-slate-400">
                                    Informe quando e por onde o cliente foi respondido. Se a mensagem enviada foi diferente da prévia, cole no campo acima o texto que foi mandado.
                                </p>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                    <label htmlFor="resposta-por-fora-data" className="block text-[11px] font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wider mb-1">Data do envio</label>
                                    <input id="resposta-por-fora-data" type="date" value={dataPorFora} min={dataMinima || undefined} max={hojeSP()}
                                        onChange={e => setDataPorFora(e.target.value)}
                                        className="w-full border border-gray-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm bg-white dark:bg-slate-900 dark:text-slate-100 focus:border-primary outline-none" />
                                </div>
                                <div>
                                    <label htmlFor="resposta-por-fora-meio" className="block text-[11px] font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wider mb-1">Por onde</label>
                                    <select id="resposta-por-fora-meio" value={meioPorFora} onChange={e => setMeioPorFora(e.target.value as MeioRespostaManual)}
                                        className="w-full border border-gray-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm bg-white dark:bg-slate-900 dark:text-slate-100 focus:border-primary outline-none">
                                        <option value="">Selecione...</option>
                                        {(Object.keys(MEIO_RESPOSTA_LABEL) as MeioRespostaManual[]).map(m => (
                                            <option key={m} value={m}>{MEIO_RESPOSTA_LABEL[m]}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div className="flex items-center justify-end gap-2">
                                <button type="button" onClick={() => setPorFora(false)} disabled={salvando}
                                    className="px-3 py-2 rounded-lg text-sm font-semibold text-gray-600 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800">
                                    Voltar
                                </button>
                                <button type="button" onClick={registrar} disabled={salvando || !texto.trim() || !meioPorFora}
                                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-white text-sm font-bold hover:opacity-90 disabled:opacity-50">
                                    {salvando ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                                    {salvando ? 'Registrando...' : 'Registrar resposta'}
                                </button>
                            </div>
                        </div>
                    ) : null}
                </div>

                <div className="p-4 md:p-6 border-t border-gray-100 dark:border-slate-700/50 bg-gray-50/50 dark:bg-slate-800/50 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <p className="text-[11px] text-gray-500 dark:text-slate-400">
                        {resultadoEnvio
                            ? resultadoEnvio.map(e => rotuloResultado(e.canal, e.resultado)).join(' · ')
                            : 'O envio sai por e-mail e WhatsApp. "Enfileirado" quer dizer que a operadora aceitou, não que o cliente já leu.'}
                    </p>
                    <div className="flex items-center gap-2 flex-wrap sm:justify-end sm:shrink-0">
                        <button type="button" onClick={copiar}
                            className="inline-flex flex-1 sm:flex-none items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 text-gray-700 dark:text-slate-200 text-sm font-semibold hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors">
                            <Copy size={15} /> Copiar
                        </button>
                        <button type="button" onClick={() => setPorFora(true)} disabled={salvando || enviando || !texto.trim() || porFora}
                            title="Não envia nada — só grava que você já respondeu o cliente por fora, com a data e o meio reais."
                            className="inline-flex flex-1 sm:flex-none items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 text-gray-700 dark:text-slate-200 text-sm font-semibold hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors disabled:opacity-50">
                            Marcar como respondido
                        </button>
                        <button type="button" onClick={enviarAoCliente} disabled={salvando || enviando || !texto.trim()}
                            title="Envia por e-mail e WhatsApp e registra o resultado de cada canal."
                            className="inline-flex flex-1 sm:flex-none items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-primary text-white text-sm font-bold hover:opacity-90 transition-colors disabled:opacity-50">
                            {enviando ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
                            {enviando ? 'Enviando...' : 'Enviar ao cliente'}
                        </button>
                    </div>
                </div>
            </div>
        </div>,
        document.body,
    );
};

export default RespostaCancelamentoModal;
